import React from 'react';
import { installServerAnimationFrameDriver } from './ssrAnimationFrameDriver';
import { QueryStringFeatures } from './QueryStringFeatures';
import type { RoutePath, SetupOverrides } from '@shared/core';
import { setup, useTranslation } from '@shared/core';
import { Setup } from './Setup';
import { Route, Switch } from '@react-router';
import { ErrorBoundary } from './ErrorBoundary';
import { ssrRoutes } from './router.ssr';
import { Helmet } from 'react-helmet-async';
import { useFreezeNavigation } from '@shared/utils/hooks/useFreezeNavigation';
import { EventRedirect } from '@shared/components/EventRedirect';
import { enableMapSet } from 'immer';
import { AccountRedirect } from '@shared/components/AccountRedirect';
import { FinishLoadingEventInfo } from '@shared/utils/eventInfo';
import { useI18nLocaleSyncer } from '@shared/hooks/useGeoLocale';
import type { Variants } from '@amplitude/experiment-js-client';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import type { EventWebsiteState } from '@graphql/generated-base';
import type { InitialThemeStyles } from '@shared/utils/styleApplicator/ThemeStylesProvider';

enableMapSet();

// Gives react-spring a frame source on the server so springs started during SSR finish and
// release their render graphs (PRNT-4632). Called from the ssrApp entry module because the
// leaked frame-loop Set lives in this bundle's react-spring instance; no-op in the browser.
installServerAnimationFrameDriver();

export type AppProps = Readonly<
  Partial<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    routes: Array<RoutePath<any>>;
    helmetContext: object;
    setupOverrides?: SetupOverrides;
    defaultScreen?: 'mobile' | 'tablet' | 'desktop';
    initialEventHandleForEventInfoProvider?: string;
    initialEventId?: string;
    initialAmplitudeFeatureFlagVariants?: Variants;
    initialThemeStyles?: InitialThemeStyles;
    initialEventFeatureFlags?: ReadonlyArray<string>;
    initialEventWebsiteState?: EventWebsiteState;
    initialEventType?: string;
  }>
>;

/** Routes the root Switch renders ahead of the app route table. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const rootRoutes: Array<RoutePath<any>> = [
  {
    path: '/eventredirect/:eventId',
    exact: true,
    component: ({ match, staticContext, location }) =>
      match ? (
        <EventRedirect
          eventId={match.params.eventId}
          location={location}
          staticContext={staticContext}
        />
      ) : null,
  },
  {
    path: '/account-redirect',
    exact: true,
    component: () => <AccountRedirect />,
  },
];

const Routes = ({ routes = ssrRoutes }: Pick<AppProps, 'routes'>) => {
  const { location } = useFreezeNavigation();
  return (
    <Switch location={location}>
      {rootRoutes.map(route => (
        <Route
          key={route.path}
          path={route.path}
          exact={route.exact}
          render={route.component}
        />
      ))}
      {routes.map(route => (
        <Route
          key={route.path}
          path={route.path}
          render={route.component}
        />
      ))}
    </Switch>
  );
};

const AppRoot: React.FC = ({ children }) => {
  useI18nLocaleSyncer();

  const { t2 } = useTranslation('ssrServer');
  const translations = t2('metatags');

  return (
    <>
      <QueryStringFeatures />
      <Helmet>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1"
        />
        <meta
          httpEquiv="Content-Type"
          content="text/html; charset=utf-8"
        />
        <meta
          property="og:title"
          content={translations.title}
        />
        <meta
          property="og:site_name"
          content="Joy"
        />
      </Helmet>
      {children}
    </>
  );
};

const App: React.FC<AppProps> = ({
  defaultScreen,
  routes,
  helmetContext = {},
  initialEventHandleForEventInfoProvider,
  initialEventId,
  initialAmplitudeFeatureFlagVariants,
  initialThemeStyles,
  initialEventWebsiteState,
  initialEventFeatureFlags,
  initialEventType,
  setupOverrides,
}) => {
  const setupConfig = setup(setupOverrides);
  return (
    <>
      <ErrorBoundary>
        <Setup
          {...setupConfig}
          defaultScreen={defaultScreen}
          initialEventHandleForEventInfoProvider={initialEventHandleForEventInfoProvider}
          initialEventId={initialEventId}
          initialAmplitudeFeatureFlagVariants={initialAmplitudeFeatureFlagVariants}
          initialThemeStyles={initialThemeStyles}
          initialEventWebsiteState={initialEventWebsiteState}
          initialEventFeatureFlags={initialEventFeatureFlags}
          initialEventType={initialEventType}
          helmetContext={helmetContext}
        >
          <AppRoot>
            <Routes routes={routes} />
            <FinishLoadingEventInfo />
          </AppRoot>
        </Setup>
      </ErrorBoundary>
    </>
  );
};

export default App as React.FC<AppProps>;
